import { vec3 } from 'gl-matrix';
import { Grid } from '../../engine/data/common/Grid';
import { GenerationModelDetails } from '../dicom/dicomBase';
import {
    DVHVolumeUnits,
    Dose,
    DoseSummationType,
    DoseType,
    DoseUnit,
} from '../dicom/dicomDose';
import { Roi } from '../dicom/dicomstructureset';
import { Dvh } from './dvh';
import { Plan } from './plan';
import { StructureSet } from './structureset';

/**
 * {@link DoseMatrix} is a container for dose information.
 */
export class DoseMatrix {
    /**
     * Create a {@link DoseMatrix} from DICOM object
     * @param dose DICOM dose object
     * @returns DoseMatrix
     */
    static fromDose(dose: Dose): DoseMatrix {
        const res = vec3.fromValues(
            dose.pixelSpacing[0],
            dose.pixelSpacing[1],
            dose.sliceResolution
        );
        const size = vec3.fromValues(dose.cols, dose.rows, dose.frames);
        return new DoseMatrix(
            dose,
            Grid.fromDicom(
                size,
                res,
                dose.imagePosition,
                dose.imageOrientation
            ),
            dose.doseUnits,
            dose.sopInstanceUID,
            dose.doseType,
            dose.doseSummationType,
            dose.doseGridScaling,
            dose.referencedRtPlanUid
        );
    }

    public constructor(
        private readonly _dicomDose: Dose,
        grid: Grid,
        doseUnits: DoseUnit,
        dicomUid: string,
        doseType: DoseType,
        doseSummationType: DoseSummationType,
        doseGridScaling: number,
        private readonly _referencedRtPlanUid: string
    ) {
        this.grid = grid;
        this.doseUnits = doseUnits;
        this.dicomUid = dicomUid;
        this.doseType = doseType;
        this.doseSummationType = doseSummationType;
        this.doseGridScaling = doseGridScaling;
    }

    public get generationModel(): GenerationModelDetails | undefined {
        return this._dicomDose.generationModel;
    }

    public get isNew(): boolean {
        return this._dicomDose.isNew;
    }

    public get isExported(): boolean {
        return this._dicomDose.isExported;
    }

    public setExported(): void {
        this._dicomDose.setExported();
    }

    public get plan(): Plan | null {
        return this._plan;
    }

    public set plan(plan: Plan | null) {
        this._plan = plan;
    }

    public get structureSet(): StructureSet | null {
        if (this._plan) {
            return this._plan.structureSet;
        }
        return this._structureSet;
    }

    public set structureSet(structureSet: StructureSet | null) {
        if (this._plan && this._plan.structureSet !== structureSet) {
            throw new Error(
                'Cannot set structure set when plan has different structure set'
            );
        }
        if (!this._plan) {
            this._structureSet = structureSet;
        }
    }

    public get dicomDose(): Dose {
        return this._dicomDose;
    }

    public referencesPlanUid(uid: string): boolean {
        return uid === this._referencedRtPlanUid;
    }

    private _plan: Plan | null = null;

    // direct structure set reference, null if we have a plan reference instead
    private _structureSet: StructureSet | null = null;

    public readonly grid: Grid;

    public readonly doseUnits: DoseUnit;

    public readonly dicomUid: string;

    public readonly doseType: DoseType;

    public readonly doseSummationType: DoseSummationType;

    private readonly doseGridScaling: number;

    public get pixelData(): Uint32Array {
        return this._dicomDose.pixelData;
    }

    private _realPixels?: Float32Array;

    public get realPixels() {
        if (!this._realPixels) {
            this._realPixels = new Float32Array(this.pixelData.values()).map(
                (v) => v * this.doseGridScaling
            );
        }
        return this._realPixels;
    }

    public get doseMaxGy(): number {
        return this.realPixels.reduce((a, b) => Math.max(a, b), -Infinity);
    }

    private _dvhs: Map<Roi, Dvh> = new Map();

    public getDvh(roi: Roi): Dvh | undefined {
        if (!this._dvhs.has(roi)) {
            this._loadDvhs();
        }
        return this._dvhs.get(roi);
    }

    public setDvh(roi: Roi, dvh: Dvh) {
        this._dvhs.set(roi, dvh);
    }

    public getDvhs(): Map<Roi, Dvh> {
        this._loadDvhs();
        return this._dvhs;
    }

    private _loadDvhs() {
        if (this.structureSet) {
            this.structureSet.rois.forEach((roi) => {
                const dvhPoints = [];
                const dicomDvh = this._dicomDose.dvhSequence.find(
                    (d) => d.referencedRoiNumber === roi.roiNumber
                );
                if (dicomDvh) {
                    const binWidth = dicomDvh.dvhData[0];
                    const toPercentageFactor = 100 / dicomDvh.dvhData[1];
                    for (let i = 0; i < dicomDvh.dvhData.length; i += 2) {
                        if (dicomDvh.dvhData[i] !== binWidth) {
                            console.warn(
                                `DVH bin width mismatch, skipping ROI ${roi.name}`
                            );
                            return;
                        }
                        if (dicomDvh.dvhVolumeUnits == DVHVolumeUnits.PERCENT) {
                            dvhPoints.push(dicomDvh.dvhData[i + 1]);
                        } else if (
                            dicomDvh.dvhVolumeUnits == DVHVolumeUnits.CM3
                        ) {
                            dvhPoints.push(
                                dicomDvh.dvhData[i + 1] * toPercentageFactor
                            );
                        }
                    }
                    this._dvhs.set(
                        roi,
                        new Dvh(
                            dvhPoints,
                            binWidth,
                            dicomDvh.dvhMaximumDose,
                            dicomDvh.dvhMinimumDose,
                            dicomDvh.dvhMeanDose
                        )
                    );
                }
            });
        }
    }
}
